import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, addDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useScheduleActions } from '../../hooks/useSchedule';
import { useTeam } from '../../hooks/useTeam';
import Button from '../common/Button';
import Select from '../common/Select';
import DatePicker from '../common/DatePicker';
import { toast } from 'react-hot-toast';
import { getCurrentUser } from '../../lib/auth/session';
import { supabase } from '../../lib/supabase/client';
import { ScheduleStatus } from '../../types/schedule';

// Define the form schema using Zod
const scheduleFormSchema = z.object({
  team_id: z.string().nonempty('Team is required'),
  start_date: z.date({ required_error: 'Start date is required' }),
  end_date: z.date({ required_error: 'End date is required' })
    .refine(date => date instanceof Date, {
      message: 'End date is required',
    }),
  status: z.enum(['draft', 'preferences_open', 'preferences_closed', 'published'])
    .default('draft'),
});

type ScheduleFormValues = z.infer<typeof scheduleFormSchema>;

interface ScheduleFormProps {
  teamId?: string;
  onSuccess?: (scheduleId: string) => void;
}

const ScheduleForm: React.FC<ScheduleFormProps> = ({ teamId, onSuccess }) => {
  const navigate = useNavigate();
  const { createNewSchedule, isLoading } = useScheduleActions();
  const { team } = useTeam(teamId);
  const [submitting, setSubmitting] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  
  // Verify current user on component mount
  useEffect(() => {
    const checkAuthState = async () => {
      try {
        if (!supabase) {
          console.error('Supabase client not initialized');
          return;
        }
        
        // Get current auth state
        const { data } = await supabase.auth.getSession();
        console.log('Current auth session:', data);
        
        if (data.session?.user) {
          console.log('User is authenticated:', data.session.user);
          setUserId(data.session.user.id);
        } else {
          console.warn('No authenticated user found');
        }
      } catch (error) {
        console.error('Error checking auth state:', error);
      }
    };
    
    checkAuthState();
  }, []);
  
  const { control, handleSubmit, watch, formState: { errors } } = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleFormSchema),
    defaultValues: {
      team_id: teamId || '',
      status: 'draft',
      start_date: new Date(),
      end_date: addDays(new Date(), team?.schedulingPeriodDays || 28),
    }
  });

  const startDate = watch('start_date');

  const onSubmit = async (data: ScheduleFormValues) => {
    try {
      setSubmitting(true);
      
      // Check if we already know the user ID from our effect
      let authUserId = userId;
      
      // If not, try to get it directly
      if (!authUserId) {
        const user = await getCurrentUser();
        if (!user) {
          toast.error('You must be logged in to create a schedule');
          return;
        }
        authUserId = user.id;
      }
      
      // Properly format the data to match what Supabase expects
      const scheduleData = {
        team_id: data.team_id,
        created_by: authUserId,
        start_date: format(data.start_date, 'yyyy-MM-dd'),
        end_date: format(data.end_date, 'yyyy-MM-dd'),
        status: 'draft' as ScheduleStatus, // Cast to ScheduleStatus
        start_time: `${format(data.start_date, 'yyyy-MM-dd')}T08:00:00Z`, // Proper timestamp format with timezone
        end_time: `${format(data.end_date, 'yyyy-MM-dd')}T23:59:59Z`, // Add end_time with the end of day
      };
      
      console.log('Creating schedule with data:', scheduleData);
      console.log('Data types:', {
        team_id: typeof scheduleData.team_id,
        created_by: typeof scheduleData.created_by,
        start_date: typeof scheduleData.start_date,
        end_date: typeof scheduleData.end_date,
        status: typeof scheduleData.status,
        start_time: typeof scheduleData.start_time,
        end_time: typeof scheduleData.end_time,
      });
      
      if (!scheduleData.team_id || !scheduleData.created_by) {
        toast.error('Missing required fields (team or user ID)');
        console.error('Missing required fields:', {
          team_id: scheduleData.team_id,
          created_by: scheduleData.created_by
        });
        return;
      }
      
      try {
        const newSchedule = await createNewSchedule(scheduleData);
        
        if (newSchedule) {
          toast.success('Schedule created successfully');
          
          if (onSuccess) {
            onSuccess(newSchedule.id);
          } else {
            navigate(`/schedules/${newSchedule.id}`);
          }
        } else {
          toast.error('Failed to create schedule - no data returned');
        }
      } catch (supabaseError: unknown) {
        console.error('Supabase error creating schedule:', supabaseError);
        // Type assertion for a more specific error type
        const errorWithDetails = supabaseError as Error & { 
          details?: string; 
          hint?: string;
        };
        
        // Log the full error details
        if (errorWithDetails.message) {
          console.error('Error message:', errorWithDetails.message);
        }
        
        if (errorWithDetails.details) {
          console.error('Error details:', errorWithDetails.details);
        }
        
        if (errorWithDetails.hint) {
          console.error('Error hint:', errorWithDetails.hint);
          toast.error(`Error hint: ${errorWithDetails.hint}`);
        } else {
          toast.error(`Failed to create schedule: ${errorWithDetails.message || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error creating schedule:', error);
      toast.error('Failed to create schedule. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-3xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="col-span-1 md:col-span-2">
          <Controller
            name="team_id"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label="Team"
                error={errors.team_id?.message}
                disabled={!!teamId || isLoading || submitting}
                options={[{ value: team?.id || '', label: team?.name || 'Current Team' }]}
              />
            )}
          />
        </div>

        <div>
          <Controller
            name="start_date"
            control={control}
            render={({ field }) => (
              <DatePicker
                label="Start Date"
                selected={field.value}
                onChange={field.onChange}
                error={errors.start_date?.message}
                disabled={isLoading || submitting}
              />
            )}
          />
        </div>

        <div>
          <Controller
            name="end_date"
            control={control}
            render={({ field }) => (
              <DatePicker
                label="End Date"
                selected={field.value}
                onChange={field.onChange}
                error={errors.end_date?.message}
                disabled={isLoading || submitting}
                minDate={startDate ? addDays(startDate, 1) : undefined}
              />
            )}
          />
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate(-1)}
          disabled={isLoading || submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading || submitting}
        >
          Create Schedule
        </Button>
      </div>
    </form>
  );
};

export default ScheduleForm; 